import React, { useState } from 'react';
import { Button, Modal } from '../ui';
import type { ReporteBackend } from '../../services/reportesService';
import { useTranslation } from 'react-i18next';
import { formatDateSafe } from '../../utils/dateUtils';

interface ModalOpcionesPDFProps {
  isOpen: boolean;
  reporte: ReporteBackend | null;
  onClose: () => void;
  onGenerar: (opciones: {
    tipo: 'frontend' | 'mapa' | 'filtros';
    incluirMapa: boolean;
    zoom: number;
  }) => void;
}

const ModalOpcionesPDF: React.FC<ModalOpcionesPDFProps> = ({
  isOpen,
  reporte,
  onClose,
  onGenerar,
}) => {
  const { t } = useTranslation();
  const [incluirMapa, setIncluirMapa] = useState(true);
  const [zoom, setZoom] = useState(14);
  const [generando, setGenerando] = useState(false);

  const handleGenerar = async () => {
    setGenerando(true);
    
    try {
      await onGenerar({
        tipo: 'frontend', // Siempre usar el mismo tipo
        incluirMapa,
        zoom,
      });
    } finally {
      setGenerando(false);
    }
  };

  if (!reporte) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" title={t('pdf.options_title')}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Generar PDF - Reporte {reporte.numero}
            </h2>
            <p className="text-sm text-gray-600">
              Máquina: {reporte.maquina_codigo} | 
              Fecha: {formatDateSafe(reporte.fecha)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Opciones simplificadas */}
        <div className="space-y-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900">Opciones de Impresión</h3>
          
          {/* Opción principal: Con o sin mapa */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <input
                type="radio"
                id="sin-mapa"
                name="tipo-impresion"
                checked={!incluirMapa}
                onChange={() => setIncluirMapa(false)}
                className="text-blue-600"
              />
              <label htmlFor="sin-mapa" className="text-sm font-medium text-gray-700 flex items-center">
                <svg className="w-5 h-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('reports.without_map')}
              </label>
            </div>
            
            <div className="flex items-center space-x-3">
              <input
                type="radio"
                id="con-mapa"
                name="tipo-impresion"
                checked={incluirMapa}
                onChange={() => setIncluirMapa(true)}
                className="text-blue-600"
              />
              <label htmlFor="con-mapa" className="text-sm font-medium text-gray-700 flex items-center">
                <svg className="w-5 h-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m-6 3l6-3" />
                </svg>
                {t('reports.with_map')}
              </label>
            </div>
          </div>

          {/* Configuración de zoom (solo si incluye mapa) */}
          {incluirMapa && (
            <div className="space-y-2 pl-8 border-l-2 border-blue-200">
              <label className="block text-sm font-medium text-gray-700">
                Nivel de zoom del mapa: {zoom}
              </label>
              <input
                type="range"
                min="10"
                max="18"
                value={zoom}
                onChange={(e) => setZoom(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>10 (Vista general)</span>
                <span>14 (Balanceado)</span>
                <span>18 (Detalle)</span>
              </div>
            </div>
          )}
        </div>

        {/* Información del reporte */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-gray-900 mb-2">Información del reporte</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Horas trabajadas:</span>
              <span className="ml-2 font-medium">{reporte.horas_trabajadas}h</span>
            </div>
            <div>
              <span className="text-gray-600">Total faenas:</span>
              <span className="ml-2 font-medium">{reporte.total_faenas}</span>
            </div>
            <div>
              <span className="text-gray-600">Área trabajada:</span>
              <span className="ml-2 font-medium">{reporte.total_area_trabajada} ha</span>
            </div>
            <div>
              <span className="text-gray-600">Con ubicación GPS:</span>
              <span className={`ml-2 font-medium ${reporte.tiene_ubicacion ? 'text-green-600' : 'text-gray-400'}`}>
                {reporte.tiene_ubicacion ? 'Sí' : 'No'}
              </span>
            </div>
          </div>
        </div>

        {/* Vista previa simplificada */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-blue-900 mb-2">Configuración seleccionada</h4>
          <div className="text-sm text-blue-800">
            <p><strong>Tipo:</strong> {incluirMapa ? 'Con mapa' : 'Solo datos'}</p>
            {incluirMapa && (
              <p><strong>Zoom del mapa:</strong> {zoom}</p>
            )}
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex items-center justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={generando}
          >
            {t('common.cancel')}
          </Button>

          <Button
            onClick={handleGenerar}
            disabled={generando}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {generando ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('pdf.generating')}
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3" />
                </svg>
                {t('pdf.generate')}
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ModalOpcionesPDF;